import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Text,
} from "@chakra-ui/layout";
import {
  Button,
  NumberInput as ChakraNumberInput,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInputField,
  Tab,
  TabList,
  Table,
  TableContainer,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { BankListStoreContext } from "@src/components/modules/bank";
import {
  Card,
  DataTable,
  InputDatePicker,
  LoadingOverlay,
  NumberInput,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { FiltersPanelSearch } from "@src/utils/components/filters/components/filters-panel-search";
import { currency, formatDate } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import { useContext } from "react";
import type { MatchPaymentTableState } from "./store/table-state";

export const MatchPaymentModal = observer(function MatchPaymentModal() {
  const { matchPaymentModalStore: store } = useContext(BankListStoreContext);

  return (
    <Modal
      isCentered
      isOpen={store.disclosure.isOpen}
      onClose={store.disclosure.onClose}
      scrollBehavior="inside"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent minW="95vw" h="90vh" minH="90vh">
        <ModalHeader>
          <Trans>Match payment</Trans>
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <Box mb="4">
            <Card bg="gray.50">
              {store.selectedAccountMove && (
                <DataTable>
                  <DataTable.Body
                    tableKey={`${store.tableKey}-selected-account-move`}
                    columns={store.columns}
                    data={[store.selectedAccountMove]}
                    enableColumnVisibilityControl={false}
                  />
                </DataTable>
              )}
            </Card>
            {store.selectedDocuments.length > 0 && (
              <Grid
                templateColumns={
                  store.selectedDocumentsIncludeForeignCurrency
                    ? "1fr"
                    : "0.775fr 0.225fr"
                }
                mt="4"
                gap="4"
              >
                <GridItem>
                  <Card h="full" pos="relative" pt="4">
                    <Heading fontSize="xl" pl="6">
                      <Trans>Documents</Trans>
                    </Heading>
                    <TableContainer>
                      <Table>
                        <Thead>
                          <Tr>
                            <Th>
                              <Trans>Amount</Trans>
                            </Th>
                            {store.selectedDocumentsIncludeForeignCurrency && (
                              <>
                                <Th>
                                  <Trans>Exchange rate</Trans>
                                </Th>
                                <Th>
                                  <Trans>Converted amount</Trans>
                                </Th>
                              </>
                            )}
                            <Th>
                              <Trans>Type</Trans>
                            </Th>
                            <Th>
                              <Trans>Document no</Trans>
                            </Th>
                            <Th>
                              <Trans>Date</Trans>
                            </Th>
                            <Th />
                          </Tr>
                        </Thead>

                        <Tbody>
                          {store.selectedDocuments.map((document) =>
                            document.isEditEnabled.value ? (
                              <Tr
                                key={`${document.$.documentId.$}-editable`}
                                px="4"
                              >
                                <Td>
                                  <NumberInput
                                    currency
                                    value={document.amountInputValue}
                                    onChange={document.handleAmountChange}
                                    suffix={document.currency.code}
                                    error={document.$.amount.error}
                                  />
                                </Td>
                                {document.hasForeignCurrency ? (
                                  <>
                                    <Td>
                                      <NumberInput
                                        currency
                                        value={document.$.exchangeRate.value}
                                        onChange={
                                          document.$.exchangeRate.onChange
                                        }
                                        prefix={document.exchangeRatePrefix}
                                        suffix={document.currency.code}
                                        error={document.$.exchangeRate.error}
                                        inputStyle={{
                                          paddingInlineStart: "16",
                                        }}
                                        prefixStyles={{
                                          minW: "fit-content",
                                        }}
                                      />
                                    </Td>
                                    <Td>
                                      {store.selectedAccountMove
                                        ? currency.formatByCurrency(
                                            document.denominatedAmountInMoveCurrency,
                                            store.selectedAccountMove?.currency,
                                          )
                                        : null}
                                    </Td>
                                  </>
                                ) : store.selectedDocumentsIncludeForeignCurrency ? (
                                  <>
                                    <Td />
                                    <Td />
                                  </>
                                ) : null}
                                <Td>{document.documentTypeReadable}</Td>
                                <Td>{document.documentNo}</Td>
                                <Td>
                                  <InputDatePicker
                                    minW="90px"
                                    onChange={document.setDate}
                                    selected={document.$.date.value}
                                    error={document.$.date.error}
                                  />
                                </Td>
                                <Td>
                                  <HStack justify="end" spacing="2">
                                    <Button
                                      colorScheme="grey"
                                      onClick={document.handleEditCancel}
                                      size="sm"
                                      variant="outline"
                                    >
                                      <Trans>Cancel</Trans>
                                    </Button>
                                    <Button
                                      onClick={document.handleEditSave}
                                      size="sm"
                                    >
                                      <Trans>Save</Trans>
                                    </Button>
                                  </HStack>
                                </Td>
                              </Tr>
                            ) : (
                              <Tr
                                key={`${document.$.documentId.$}-noneditable`}
                                h={
                                  store.selectedDocuments.length === 1
                                    ? "16"
                                    : undefined
                                }
                                px="4"
                              >
                                <Td>
                                  {currency.formatByCurrency(
                                    document.$.amount.$,
                                    document.currency,
                                  )}
                                </Td>
                                {document.hasForeignCurrency ? (
                                  <>
                                    <Td>
                                      {document.exchangeRatePrefix}
                                      &nbsp;
                                      {document.formattedExchangeRate}
                                    </Td>
                                    <Td>
                                      {store.selectedAccountMove
                                        ? currency.formatByCurrency(
                                            document.denominatedAmountInMoveCurrency,
                                            store.selectedAccountMove?.currency,
                                          )
                                        : null}
                                    </Td>
                                  </>
                                ) : store.selectedDocumentsIncludeForeignCurrency ? (
                                  <>
                                    <Td />
                                    <Td />
                                  </>
                                ) : null}
                                <Td>{document.documentTypeReadable}</Td>
                                <Td>{document.documentNo}</Td>
                                <Td>{formatDate(document.$.date.$)}</Td>
                                <Td>
                                  <Flex justify="end">
                                    <IconButton
                                      aria-label={t`Selected document value edit`}
                                      colorScheme="grey"
                                      icon={<Icon name="edit-02" />}
                                      onClick={() => {
                                        document.isEditEnabled.on();
                                      }}
                                      variant="ghost"
                                    />
                                    <IconButton
                                      aria-label={t`Unselect document`}
                                      colorScheme="red"
                                      icon={<Icon name="trash-03" />}
                                      onClick={() => {
                                        store.removeDocument(document);
                                      }}
                                      variant="ghost"
                                    />
                                  </Flex>
                                </Td>
                              </Tr>
                            ),
                          )}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Card>
                </GridItem>

                <GridItem>
                  <Card h="full" px="6" py="4">
                    <Box pos="sticky" top="0">
                      <Heading fontSize="xl">
                        <Trans>Balance</Trans>
                      </Heading>
                      <Text
                        mt="2"
                        fontSize="2xl"
                        fontWeight="bold"
                        color={store.balanceColor}
                      >
                        {store.balance !== 0 &&
                          (!store.isBalanceNegative ? (
                            <>
                              <Trans>Left</Trans>
                              &nbsp;
                            </>
                          ) : (
                            <>
                              <Trans>Over</Trans>
                              &nbsp;
                            </>
                          ))}
                        {store.balanceFormatted}
                      </Text>

                      {store.isBalanceNegative && (
                        <Text color={store.balanceColor} mt="2">
                          <Trans>Can't match more than transaction value</Trans>
                        </Text>
                      )}
                    </Box>
                  </Card>
                </GridItem>
              </Grid>
            )}
          </Box>

          <Tabs index={store.selectedTab} onChange={store.setSelectedTab}>
            <FiltersPanelRoot filters={store.selectedTableStateProps.filters}>
              <HStack
                w="full"
                justify="space-between"
                spacing="4"
                align="center"
                mb="4"
              >
                <TabList minW="fit-content">
                  <Tab>
                    <Trans>Expenses</Trans>
                  </Tab>
                  <Tab>
                    <Trans>Outgoing invoices</Trans>
                  </Tab>
                  <Tab>
                    <Trans>Proforma invoices</Trans>
                  </Tab>
                </TabList>
                <HStack w="full" justify="space-between">
                  <FiltersPanelSearch
                    onSearch={() => {}}
                    ref={store.searchInputRef}
                    onChange={store.selectedTableStateProps.onSearch}
                  />
                  <ChakraNumberInput
                    onChange={store.handleChangeAmountFrom}
                    size="sm"
                    value={store.amountFromInputValue}
                  >
                    <NumberInputField placeholder={t`Amount from`} />
                  </ChakraNumberInput>
                  <ChakraNumberInput
                    onChange={store.handleChangeAmountTo}
                    size="sm"
                    value={store.amountToInputValue}
                  >
                    <NumberInputField placeholder={t`Amount to`} />
                  </ChakraNumberInput>
                  <FiltersPanelFilters menuListZIndex={422} />
                </HStack>
              </HStack>
            </FiltersPanelRoot>
            <TabPanels>
              <TabPanel px="0">
                <DetailTable
                  tableProps={store.expensesTableState.tableProps}
                  isLoading={store.filterOptionsFetcher.isLoading.value}
                />
              </TabPanel>
              <TabPanel px="0">
                <DetailTable
                  tableProps={store.invoicesTableState.tableProps}
                  isLoading={store.filterOptionsFetcher.isLoading.value}
                />
              </TabPanel>
              <TabPanel px="0">
                <DetailTable
                  tableProps={store.proformasTableState.tableProps}
                  isLoading={store.filterOptionsFetcher.isLoading.value}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <HStack justify="end" spacing="2">
            <Button
              colorScheme="grey"
              isLoading={store.mutator.isLoading.value}
              onClick={store.disclosure.onClose}
              variant="outline"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              isDisabled={store.isConfirmDisabled}
              isLoading={store.mutator.isLoading.value}
              onClick={store.handleConfirm}
            >
              <Trans>Confirm</Trans>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

type DetailTableProps = {
  tableProps: (typeof MatchPaymentTableState)["prototype"]["tableProps"];
  isLoading: boolean;
};
export type DetailTableCustomProps = {
  selectedRows: () => string[];
  onMatchClick: (selectedIds: string[]) => void;
};
const DetailTable = observer(function DetailTable({
  tableProps,
  isLoading,
}: DetailTableProps) {
  return (
    <Box w="full">
      <LoadingOverlay
        isLoading={tableProps.isLoading || isLoading}
        position="absolute"
      >
        <DataTable>
          <DataTable.Body
            tableKey={tableProps.tableKey}
            columns={tableProps.columns}
            data={tableProps.data}
            onOrderByChange={tableProps.onOrderByChange}
            selected={tableProps.selected}
            onSelect={tableProps.onSelect}
            orderedBy={tableProps.orderedBy}
            enableRowSelection
          />
          <Pagination store={tableProps.pagination} />
        </DataTable>
      </LoadingOverlay>
    </Box>
  );
});
